import { StylesProvider } from "@caos/ca-component-library";
import { QueryClientProvider } from "@tanstack/react-query";
import { ProductActivityProvider } from "data/context/ProductActivityContext";
import { StrategyActivityProvider } from "data/context/StrategyActivityContext";
import { ConfigurationContextProvider } from "data/hooks/useConfigurationContext";
import { TabsProvider } from "data/hooks/useTabsContext";
import { useVersionCheck } from "data/hooks/useVersionCheck";
import { useApplicationTweak } from "data/store/ApplicationTweak";
import { useAuthStore } from "data/store/Auth";
import { getLoggedInPerson, usePersonStore } from "data/store/Person";
import { queryClient } from "queryClient";
import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ScopedToastContainer from "ui/components/feedback/ScopedToastContainer";
import { SessionProvider } from "ui/components/session/SessionProvider";
import HomeDashboardSkeleton from "ui/components/surfaces/skeleton/HomeDashboardSkeleton";
import CreationSpeedDial from "ui/partials/creation/CreationSpeedDial";
import NavBar from "ui/partials/nav-bar/NavBar";
import Theme from "ui/Theme";
import { getSafeRedirectPath } from "utils/navigation";

function App() {
  const theme = useApplicationTweak((state) => state.theme);
  const isVersionChecked = useVersionCheck();

  const { isReadOnly, loading: isLoadingRoles, roles } = useAuthStore();
  const personData = usePersonStore((state) => state.data);
  const userDepartment = personData?.department;
  const location = useLocation();
  const navigate = useNavigate();

  const urlsToHideSpeedDialRegex = [
    /\/meeting\/new/,
    /\/meeting\/\d+\/edit/,
    /\/comment\/new/,
    /\/comment\/\d+\/edit/,
    /\/annual-meeting-note\/new/,
    /\/annual-meeting-note\/\d+\/edit/,
    /product-details\/[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}\/edit/i,
    /strategy-details\/\d+\/edit/,
    /strategy-details\/\d+\/fund\/\d+\/(idd|ioi)(\/\d+)?/,
    /\/bulk-move\/new/,
  ];
  const hideCreationSpeedDial = urlsToHideSpeedDialRegex.some(
    (regex) => regex.test(location.pathname) || isReadOnly,
  );

  useEffect(() => {
    getLoggedInPerson();
  }, []);

  useEffect(() => {
    if (!isVersionChecked) return;

    const storedPath = localStorage.getItem("LOGIN_REDIRECT_PATH");
    if (!storedPath) return;

    localStorage.removeItem("LOGIN_REDIRECT_PATH");

    const safePath = getSafeRedirectPath(storedPath);

    if (safePath !== "/") {
      navigate(safePath, { replace: true });
    }
  }, [isVersionChecked, navigate]);

  if (!isVersionChecked) {
    return <HomeDashboardSkeleton />;
  }

  return (
    <Theme paletteMode={theme} global>
      <StylesProvider>
        <QueryClientProvider client={queryClient}>
          <ConfigurationContextProvider
            isMicrofrontendRemote={false}
            canNavigate
            userRole={roles}
            userDepartment={userDepartment}
          >
            <SessionProvider>
              <TabsProvider>
                <ProductActivityProvider>
                  <StrategyActivityProvider>
                    <div className="App">
                      <NavBar />
                      <Outlet />
                      {!hideCreationSpeedDial && !isLoadingRoles && (
                        <CreationSpeedDial />
                      )}
                    </div>
                    <ScopedToastContainer limit={5} />
                  </StrategyActivityProvider>
                </ProductActivityProvider>
              </TabsProvider>
            </SessionProvider>
          </ConfigurationContextProvider>
        </QueryClientProvider>
      </StylesProvider>
    </Theme>
  );
}

export default App;
